import { Controller } from "@hotwired/stimulus"

/**
 * Clip List Controller
 * Handles drag-and-drop reordering of clip cards.
 * Attached to the clips container, listens for drag events on child .clip-card elements.
 */
export default class extends Controller {
  static values = { reorderUrl: String }

  dragStart(event) {
    const card = event.target.closest(".clip-card")
    if (!card) return

    this.draggedCard = card
    card.style.opacity = "0.4"
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", card.dataset.clipId)
  }

  dragEnd(event) {
    const card = event.target.closest(".clip-card")
    if (card) card.style.opacity = "1"

    // Remove all drop indicators
    this.element.querySelectorAll(".clip-card").forEach(c => {
      c.classList.remove("border-t-2", "border-b-2", "border-indigo-500")
    })

    this.draggedCard = null
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const card = event.target.closest(".clip-card")
    if (!card || card === this.draggedCard) return

    // Remove previous indicators
    this.element.querySelectorAll(".clip-card").forEach(c => {
      c.classList.remove("border-t-2", "border-b-2", "border-indigo-500")
    })

    // Show drop indicator
    const rect = card.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2
    if (event.clientY < midpoint) {
      card.classList.add("border-t-2", "border-indigo-500")
    } else {
      card.classList.add("border-b-2", "border-indigo-500")
    }
  }

  async drop(event) {
    event.preventDefault()

    const targetCard = event.target.closest(".clip-card")
    if (!targetCard || !this.draggedCard || targetCard === this.draggedCard) return

    // Determine position
    const rect = targetCard.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2
    const insertBefore = event.clientY < midpoint

    // Move DOM element
    if (insertBefore) {
      targetCard.parentNode.insertBefore(this.draggedCard, targetCard)
    } else {
      targetCard.parentNode.insertBefore(this.draggedCard, targetCard.nextSibling)
    }

    // Clear indicators
    this.element.querySelectorAll(".clip-card").forEach(c => {
      c.classList.remove("border-t-2", "border-b-2", "border-indigo-500")
      c.style.opacity = "1"
    })

    // Update clip numbers visually
    this.element.querySelectorAll(".clip-card").forEach((card, idx) => {
      const numberEl = card.querySelector(".text-sm.font-bold.text-gray-400")
      if (numberEl) numberEl.textContent = idx + 1
    })

    // Persist new order
    const order = Array.from(this.element.querySelectorAll(".clip-card"))
      .map(card => card.dataset.clipId)

    try {
      await fetch(this.reorderUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ order })
      })
    } catch {
      // Reload on failure to reset to server state
      window.location.reload()
    }
  }
}
